/**
 * TransportBar — Execution transport controls.
 * Buttons: Play All, Step Forward, Pause, Stop, Record.
 * Also shows a progress indicator (steps completed / total).
 */

import React from 'react';
import {
  Box, IconButton, Tooltip, Divider, LinearProgress, Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  SkipNext as StepIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  FiberManualRecord as RecordIcon,
} from '@mui/icons-material';

const TransportBar = ({
  totalSteps = 0,
  completedSteps = 0,
  isRunning = false,
  isPaused = false,
  isRecording = false,
  onPlay,
  onStep,
  onPause,
  onStop,
  onRecord,
}) => {
  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#fafafa',
      }}
    >
      {/* Play All */}
      <Tooltip title="Run All">
        <span>
          <IconButton
            size="small"
            disabled={isRunning && !isPaused}
            onClick={onPlay}
            sx={{
              bgcolor: '#4caf50',
              color: '#fff',
              '&:hover': { bgcolor: '#388e3c' },
              '&.Mui-disabled': { bgcolor: '#c8e6c9', color: '#fff' },
            }}
          >
            <PlayIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Step Forward */}
      <Tooltip title="Step Forward">
        <span>
          <IconButton
            size="small"
            disabled={isRunning && !isPaused}
            onClick={onStep}
            sx={{
              bgcolor: '#1976d2',
              color: '#fff',
              '&:hover': { bgcolor: '#1565c0' },
              '&.Mui-disabled': { bgcolor: '#bbdefb', color: '#fff' },
            }}
          >
            <StepIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Pause */}
      <Tooltip title="Pause">
        <span>
          <IconButton
            size="small"
            disabled={!isRunning || isPaused}
            onClick={onPause}
          >
            <PauseIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Stop */}
      <Tooltip title="Stop">
        <span>
          <IconButton
            size="small"
            disabled={!isRunning}
            onClick={onStop}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Record */}
      <Tooltip title={isRecording ? 'Stop Recording' : 'Record Steps'}>
        <IconButton
          size="small"
          onClick={onRecord}
          sx={{
            color: isRecording ? '#fff' : '#f44336',
            bgcolor: isRecording ? '#f44336' : 'transparent',
            '&:hover': {
              bgcolor: isRecording ? '#d32f2f' : 'rgba(244, 67, 54, 0.08)',
            },
          }}
        >
          <RecordIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Progress bar + count */}
      {totalSteps > 0 && (
        <>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ width: 80, mr: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {completedSteps}/{totalSteps}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default TransportBar;
